import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../../App";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Progress } from "../../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  DollarSign,
  Users,
  TrendingUp,
  Copy,
  CheckCircle,
  Clock,
  CreditCard,
  ExternalLink,
  Loader2,
  Settings,
  LogOut,
  Gift,
  Wallet,
  ArrowUpRight,
  Trophy,
  Medal,
  Crown,
  Heart,
  Sparkles,
  Star,
  Target,
} from "lucide-react";

const ReferralPartnerDashboard = () => {
  const { token, logout, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  
  // Payout dialog state
  const [showPayoutDialog, setShowPayoutDialog] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState("");
  const [payoutMethod, setPayoutMethod] = useState("");
  const [payoutAccountInfo, setPayoutAccountInfo] = useState("");
  const [requestingPayout, setRequestingPayout] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL;
  const FRONTEND_URL = window.location.origin;

  const fetchDashboard = useCallback(async () => {
    try {
      const [dashboardRes, leaderboardRes] = await Promise.all([
        fetch(`${API}/api/referrals/partner/dashboard`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API}/api/referrals/leaderboard`, {
          headers: { Authorization: `Bearer ${token}` },
        })
      ]);

      if (!dashboardRes.ok) {
        if (dashboardRes.status === 403) {
          toast.error("Access denied. You are not a referral partner.");
          navigate("/partner/login");
          return;
        }
        throw new Error("Failed to fetch dashboard");
      }

      const dashboardDataResponse = await dashboardRes.json();
      setDashboardData(dashboardDataResponse);
      
      if (leaderboardRes.ok) {
        const leaderboardData = await leaderboardRes.json();
        setLeaderboard(leaderboardData);
      }
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, [API, token, navigate]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === "code") {
        setCopiedCode(true);
        setTimeout(() => setCopiedCode(false), 2000);
      } else {
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2000);
      }
      toast.success(`${type === "code" ? "Referral code" : "Referral link"} copied!`);
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const handleRequestPayout = async () => {
    const amount = payoutAmount ? parseFloat(payoutAmount) : (stats.current_balance || 0);
    
    if (amount < 50) {
      toast.error("Minimum payout amount is $50");
      return;
    }

    if (amount > (stats.current_balance || 0)) {
      toast.error("Amount exceeds your available balance");
      return;
    }

    if (!payoutMethod) {
      toast.error("Please select a payout method");
      return;
    }

    if (!payoutAccountInfo.trim()) {
      toast.error("Please enter your account information");
      return;
    }

    setRequestingPayout(true);

    try {
      const params = new URLSearchParams();
      params.append("amount", amount.toString());
      params.append("payout_method", payoutMethod);
      params.append("account_info", payoutAccountInfo.trim());

      const response = await fetch(`${API}/api/referrals/partner/request-payout?${params.toString()}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Failed to request payout");
      }

      toast.success(`Payout request submitted for $${data.amount.toFixed(2)}. Processing takes 7-10 business days.`);
      setShowPayoutDialog(false);
      setPayoutAmount("");
      setPayoutMethod("");
      setPayoutAccountInfo("");
      fetchDashboard();
    } catch (error) {
      toast.error(error.message || "Failed to request payout");
    } finally {
      setRequestingPayout(false);
    }
  };

  const getReferralLink = (program = "carerevive") => {
    if (!dashboardData?.partner?.referral_code) return "";
    return `${FRONTEND_URL}/programs/${program}?ref=${dashboardData.partner.referral_code}`;
  };

  const getCareReviveLink = () => getReferralLink("carerevive");
  const getFoundationsFirstLink = () => getReferralLink("foundations-first");

  const getLeaderboardIcon = (position) => {
    switch (position) {
      case 0:
        return <Crown className="h-5 w-5 text-yellow-500" />;
      case 1:
        return <Medal className="h-5 w-5 text-gray-400" />;
      case 2:
        return <Medal className="h-5 w-5 text-amber-600" />;
      default:
        return <Trophy className="h-5 w-5 text-rnb-gray/50" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-rnb-light flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-rnb-blue mx-auto mb-4" />
          <p className="text-rnb-gray">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const stats = dashboardData?.stats || {};
  const partner = dashboardData?.partner || {};
  const referrals = dashboardData?.referrals || [];
  const pendingPayouts = dashboardData?.pending_payout_requests || [];
  const milestones = dashboardData?.milestones || {};

  const getMilestoneIcon = (milestone) => {
    switch (milestone) {
      case 5: return "🌟";
      case 10: return "🏆";
      case 20: return "💎";
      default: return "🎯";
    }
  };

  const getMilestoneTitle = (milestone) => {
    switch (milestone) {
      case 5: return "Rising Star";
      case 10: return "Champion Partner";
      case 20: return "Elite Partner";
      default: return "Partner";
    }
  };

  return (
    <div className="min-h-screen bg-rnb-light">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-rnb-teal/10 flex items-center justify-center">
                <Gift className="h-5 w-5 text-rnb-teal" />
              </div>
              <div>
                <h1 className="font-heading text-xl font-bold text-rnb-navy">
                  Partner Dashboard
                </h1>
                <p className="text-sm text-rnb-gray">Welcome back, {partner.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/partner/promo-library")}
                className="hidden sm:flex"
                data-testid="promo-library-btn"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Promo Library
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/partner/settings")}
                data-testid="partner-settings-btn"
              >
                <Settings className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Settings</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                className="text-rnb-gray hover:text-red-600"
              >
                <LogOut className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Referral Code Card */}
        <Card className="card-modern mb-8 bg-gradient-to-r from-rnb-navy to-rnb-blue text-white overflow-hidden relative">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxjaXJjbGUgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjEpIiBjeD0iMjAiIGN5PSIyMCIgcj0iMiIvPjwvZz48L3N2Zz4=')] opacity-30" />
          <CardContent className="p-6 sm:p-8 relative">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
              <div>
                <p className="text-white/70 text-sm mb-2">Your Referral Code</p>
                <div className="flex items-center gap-4 mb-4">
                  <span className="text-3xl sm:text-4xl font-bold tracking-wider" data-testid="referral-code">
                    {partner.referral_code}
                  </span>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => copyToClipboard(partner.referral_code, "code")}
                    className="bg-white/20 hover:bg-white/30 text-white border-0"
                    data-testid="copy-code-button"
                  >
                    {copiedCode ? (
                      <CheckCircle className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <p className="text-white/60 text-sm">Share your code or use the program links below</p>
              </div>
              
              {/* Program Links */}
              <div className="flex flex-col gap-3 lg:min-w-[320px]">
                <div className="p-3 bg-white/10 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">CareRevive ($750)</span>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToClipboard(getCareReviveLink(), "link")}
                        className="h-7 px-2 text-white hover:bg-white/10"
                        data-testid="copy-carerevive-link"
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => window.open(getCareReviveLink(), "_blank")}
                        className="h-7 px-2 text-white hover:bg-white/10"
                      >
                        <ExternalLink className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-white/50 truncate">{getCareReviveLink()}</p>
                </div>
                
                <div className="p-3 bg-white/10 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Foundations First ($1,250)</span>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToClipboard(getFoundationsFirstLink(), "link")}
                        className="h-7 px-2 text-white hover:bg-white/10"
                        data-testid="copy-foundations-link"
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => window.open(getFoundationsFirstLink(), "_blank")}
                        className="h-7 px-2 text-white hover:bg-white/10"
                      >
                        <ExternalLink className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-white/50 truncate">{getFoundationsFirstLink()}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="card-modern" data-testid="stat-total-referrals">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <Users className="h-5 w-5 text-rnb-blue" />
                <span className="text-xs px-2 py-1 bg-rnb-blue/10 text-rnb-blue rounded-full">
                  All Time
                </span>
              </div>
              <p className="text-3xl font-bold text-rnb-navy">{stats.total_referrals || 0}</p>
              <p className="text-sm text-rnb-gray">Total Referrals</p>
            </CardContent>
          </Card>

          <Card className="card-modern" data-testid="stat-confirmed-referrals">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
                  Confirmed
                </span>
              </div>
              <p className="text-3xl font-bold text-rnb-navy">{stats.confirmed_referrals || 0}</p>
              <p className="text-sm text-rnb-gray">Confirmed Sign-ups</p>
            </CardContent>
          </Card>

          <Card className="card-modern" data-testid="stat-total-earned">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="h-5 w-5 text-rnb-teal" />
                <span className="text-xs px-2 py-1 bg-rnb-teal/10 text-rnb-teal rounded-full">
                  Earnings
                </span>
              </div>
              <p className="text-3xl font-bold text-rnb-navy">
                ${(stats.total_earned || 0).toLocaleString()}
              </p>
              <p className="text-sm text-rnb-gray">Total Earned</p>
            </CardContent>
          </Card>

          <Card className="card-modern" data-testid="stat-current-balance">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <Wallet className="h-5 w-5 text-amber-600" />
                <span className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded-full">
                  Available
                </span>
              </div>
              <p className="text-3xl font-bold text-rnb-navy">
                ${(stats.current_balance || 0).toLocaleString()}
              </p>
              <p className="text-sm text-rnb-gray">Current Balance</p>
            </CardContent>
          </Card>
        </div>

        {/* Milestone Progress Card */}
        <Card className="card-modern mb-8 overflow-hidden" data-testid="milestone-progress-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-gradient-to-br from-yellow-400 to-amber-500">
                <Target className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-semibold text-rnb-navy">Milestone Bonuses</h3>
                <p className="text-sm text-rnb-gray">Earn extra bonuses as you hit referral milestones!</p>
              </div>
            </div>
            
            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              {Object.entries(milestones.bonuses || {5: 250, 10: 500, 20: 1000}).map(([count, bonus]) => {
                const achieved = milestones.achieved?.includes(parseInt(count));
                const isNext = milestones.next_milestone === parseInt(count);
                return (
                  <div 
                    key={count}
                    className={`relative p-4 rounded-xl border-2 transition-all ${
                      achieved 
                        ? "bg-gradient-to-br from-green-50 to-emerald-50 border-green-300" 
                        : isNext 
                          ? "bg-gradient-to-br from-yellow-50 to-amber-50 border-amber-300 ring-2 ring-amber-200" 
                          : "bg-gray-50 border-gray-200"
                    }`}
                  >
                    {achieved && (
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                        <CheckCircle className="h-4 w-4 text-white" />
                      </div>
                    )}
                    <div className="text-center">
                      <span className="text-2xl mb-1 block">{getMilestoneIcon(parseInt(count))}</span>
                      <p className="font-bold text-lg text-rnb-navy">{count} Referrals</p>
                      <p className="text-xs text-rnb-gray mb-2">{getMilestoneTitle(parseInt(count))}</p>
                      <div className={`inline-block px-3 py-1 rounded-full text-sm font-bold ${
                        achieved 
                          ? "bg-green-100 text-green-700" 
                          : "bg-amber-100 text-amber-700"
                      }`}>
                        {achieved ? "Earned!" : `+$${bonus}`}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {milestones.next_milestone && (
              <div className="bg-rnb-light rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-rnb-navy">
                    Progress to {getMilestoneIcon(milestones.next_milestone)} {milestones.next_milestone} Referrals
                  </span>
                  <span className="text-sm text-rnb-gray">
                    {stats.confirmed_referrals || 0} / {milestones.next_milestone}
                  </span>
                </div>
                <Progress 
                  value={((stats.confirmed_referrals || 0) / milestones.next_milestone) * 100} 
                  className="h-3"
                />
                <p className="text-xs text-rnb-gray mt-2 text-center">
                  {milestones.referrals_to_next} more referral{milestones.referrals_to_next !== 1 ? 's' : ''} to unlock 
                  <span className="font-bold text-amber-600"> +${milestones.next_milestone_bonus}</span> bonus!
                </p>
              </div>
            )}

            {!milestones.next_milestone && (milestones.achieved?.length > 0) && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 text-center">
                <span className="text-2xl">🎉</span>
                <p className="font-semibold text-green-700">
                  Congratulations! You&apos;ve achieved all milestone bonuses!
                </p>
                <p className="text-sm text-green-600">
                  Total bonus earnings: ${(milestones.achieved || []).reduce((sum, m) => sum + (milestones.bonuses?.[m] || 0), 0).toLocaleString()}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Recent Referrals */}
          <div className="lg:col-span-2 space-y-8">
            <Card className="card-modern">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-rnb-navy">
                  Recent Referrals
                </CardTitle>
                <CardDescription>
                  Track your referral sign-ups and their status
                </CardDescription>
              </CardHeader>
              <CardContent>
                {referrals.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 text-rnb-gray/30 mx-auto mb-4" />
                    <p className="text-rnb-gray">No referrals yet</p>
                    <p className="text-sm text-rnb-gray/70 mt-1">
                      Share your code to start earning!
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {referrals.map((referral, index) => (
                      <div
                        key={referral.signup_id || index}
                        className="flex items-center justify-between p-4 bg-rnb-light rounded-lg"
                        data-testid={`referral-item-${index}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-rnb-blue/10 flex items-center justify-center">
                            <span className="text-sm font-medium text-rnb-blue">
                              {referral.customer_name?.charAt(0) || "?"}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-rnb-navy">
                              {referral.customer_name || "Anonymous"}
                            </p>
                            <p className="text-sm text-rnb-gray">
                              {referral.program_type}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-green-600">
                            +${referral.commission?.toFixed(2) || "150.00"}
                          </p>
                          <span
                            className={`text-xs px-2 py-1 rounded-full ${
                              referral.status === "confirmed"
                                ? "bg-green-100 text-green-700"
                                : "bg-yellow-100 text-yellow-700"
                            }`}
                          >
                            {referral.status === "confirmed" ? "Confirmed" : "Pending"}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Impact Section */}
            <Card className="card-modern bg-gradient-to-br from-rnb-teal/5 to-rnb-blue/5">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-rnb-teal/10">
                    <Heart className="h-6 w-6 text-rnb-teal" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-rnb-navy mb-2">
                      Your Impact
                    </h3>
                    <p className="text-rnb-gray text-sm">
                      Every referral you make helps a healthcare practice improve their operations 
                      and patient care. Thank you for being part of our mission to transform 
                      healthcare practices across the country!
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Payout Card */}
            <Card className="card-modern">
              <CardHeader>
                <CardTitle className="font-heading text-lg text-rnb-navy flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Payouts
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-rnb-light rounded-lg">
                  <p className="text-sm text-rnb-gray mb-1">Available for payout</p>
                  <p className="text-2xl font-bold text-rnb-navy">
                    ${(stats.current_balance || 0).toFixed(2)}
                  </p>
                </div>

                {pendingPayouts.length > 0 && (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800 flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Pending payout: ${pendingPayouts[0].amount?.toFixed(2)}
                    </p>
                    <p className="text-xs text-yellow-600 mt-1">Processing takes 7-10 business days</p>
                  </div>
                )}

                <Button
                  onClick={() => setShowPayoutDialog(true)}
                  className="w-full btn-primary"
                  disabled={(stats.current_balance || 0) < 50 || pendingPayouts.length > 0}
                  data-testid="request-payout-button"
                >
                  <ArrowUpRight className="h-4 w-4 mr-2" />
                  Request Payout
                </Button>

                {(stats.current_balance || 0) < 50 && (
                  <p className="text-xs text-center text-rnb-gray">
                    Minimum payout: $50
                  </p>
                )}
                
                {pendingPayouts.length > 0 && (stats.current_balance || 0) >= 50 && (
                  <p className="text-xs text-center text-rnb-gray">
                    Please wait for pending payout to complete
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Leaderboard */}
            <Card className="card-modern">
              <CardHeader>
                <CardTitle className="font-heading text-lg text-rnb-navy flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-yellow-500" />
                  Partner Leaderboard
                </CardTitle>
                <CardDescription>Top referral partners</CardDescription>
              </CardHeader>
              <CardContent>
                {leaderboard.length === 0 ? (
                  <p className="text-sm text-rnb-gray text-center py-4">
                    Be the first on the leaderboard!
                  </p>
                ) : (
                  <div className="space-y-3">
                    {leaderboard.slice(0, 5).map((entry, index) => (
                      <div
                        key={entry.partner_id}
                        className={`flex items-center justify-between p-3 rounded-lg ${
                          entry.partner_id === partner.partner_id
                            ? "bg-rnb-teal/10 border border-rnb-teal/20"
                            : "bg-rnb-light"
                        }`}
                        data-testid={`leaderboard-entry-${index}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 flex items-center justify-center">
                            {getLeaderboardIcon(index)}
                          </div>
                          <div>
                            <p className={`font-medium ${
                              entry.partner_id === partner.partner_id
                                ? "text-rnb-teal"
                                : "text-rnb-navy"
                            }`}>
                              {entry.name}
                              {entry.partner_id === partner.partner_id && (
                                <span className="ml-2 text-xs">(You)</span>
                              )}
                            </p>
                            <p className="text-xs text-rnb-gray">
                              {entry.confirmed_referrals} referral{entry.confirmed_referrals !== 1 ? "s" : ""}
                            </p>
                          </div>
                        </div>
                        <span className="font-bold text-green-600">
                          ${entry.total_earned?.toLocaleString() || 0}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Commission Info */}
            <Card className="card-modern">
              <CardHeader>
                <CardTitle className="font-heading text-lg text-rnb-navy flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Commission Rate
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center p-4 bg-gradient-to-br from-rnb-teal/10 to-rnb-blue/10 rounded-xl">
                  <p className="text-4xl font-bold text-rnb-teal">
                    ${stats.commission_per_signup || 150}
                  </p>
                  <p className="text-sm text-rnb-gray mt-1">per confirmed sign-up</p>
                </div>
                <p className="text-xs text-rnb-gray text-center mt-4">
                  Commissions are confirmed when a referred customer completes payment for CareRevive or Foundations First.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Payout Request Dialog */}
      <Dialog open={showPayoutDialog} onOpenChange={setShowPayoutDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Payout</DialogTitle>
            <DialogDescription>
              Enter the amount and your preferred payout method. Payouts are processed within 7-10 business days.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-rnb-light rounded-lg">
              <p className="text-sm text-rnb-gray mb-1">Available balance</p>
              <p className="text-2xl font-bold text-rnb-navy">
                ${(stats.current_balance || 0).toFixed(2)}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="payout-amount">Amount</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-rnb-gray">$</span>
                <Input
                  id="payout-amount"
                  type="number"
                  min="50"
                  max={stats.current_balance || 0}
                  step="0.01"
                  value={payoutAmount}
                  onChange={(e) => setPayoutAmount(e.target.value)}
                  placeholder={`Max: ${(stats.current_balance || 0).toFixed(2)}`}
                  className="pl-7"
                  data-testid="payout-amount-input"
                />
              </div>
              <p className="text-xs text-rnb-gray">Minimum: $50 | Maximum: ${(stats.current_balance || 0).toFixed(2)}</p>
            </div>

            <div className="space-y-2">
              <Label>Payout Method</Label>
              <Select value={payoutMethod} onValueChange={setPayoutMethod}>
                <SelectTrigger data-testid="payout-method-select">
                  <SelectValue placeholder="Select payout method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="paypal">PayPal</SelectItem>
                  <SelectItem value="cashapp">Cash App</SelectItem>
                  <SelectItem value="venmo">Venmo</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="account-info">
                {payoutMethod === "paypal" && "PayPal Email"}
                {payoutMethod === "cashapp" && "Cash App $Cashtag"}
                {payoutMethod === "venmo" && "Venmo Username"}
                {!payoutMethod && "Account Information"}
              </Label>
              <Input
                id="account-info"
                value={payoutAccountInfo}
                onChange={(e) => setPayoutAccountInfo(e.target.value)}
                placeholder={
                  payoutMethod === "paypal" ? "your@email.com" :
                  payoutMethod === "cashapp" ? "$yourcastag" :
                  payoutMethod === "venmo" ? "@username" :
                  "Select a payout method first"
                }
                disabled={!payoutMethod}
                data-testid="payout-account-input"
              />
            </div>

            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800 flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Payouts are processed within 7-10 business days
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayoutDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleRequestPayout}
              disabled={requestingPayout || !payoutMethod || !payoutAccountInfo}
              className="btn-primary"
              data-testid="confirm-payout-button"
            >
              {requestingPayout ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                "Request Payout"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReferralPartnerDashboard;
